import { useEffect, useRef, useState, useCallback } from 'react'
import { PaymentLinkFormData } from '@shared/types/payment-links'
import { paymentLinkService } from '@shared/services/payment-link.service'
import { transformFormDataToPaymentLink } from '@shared/types/payment-links'
import envConfig from '@/config/env.config'

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface UseAutoSaveOptions {
  /** Enable auto-save functionality */
  enabled?: boolean
  /** Debounce delay in milliseconds (default: 2000ms = 2s) */
  debounceDelay?: number
  /** Auto-save interval in milliseconds (default: 30000ms = 30s) */
  autoSaveInterval?: number
  /** Draft ID if editing existing draft */
  draftId?: string
  /** Callback when draft is saved */
  onSaved?: (id: string) => void
  /** Callback when save fails */
  onError?: (error: Error) => void
}

export function useAutoSave(
  formData: PaymentLinkFormData,
  options: UseAutoSaveOptions = {}
) {
  const {
    enabled = true,
    debounceDelay = 2000,
    autoSaveInterval = 30000,
    draftId,
    onSaved,
    onError
  } = options

  const [status, setStatus] = useState<AutoSaveStatus>('idle')
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [currentDraftId, setCurrentDraftId] = useState<string | undefined>(draftId)

  const debounceTimerRef = useRef<NodeJS.Timeout>()
  const autoSaveTimerRef = useRef<NodeJS.Timeout>()
  const previousFormDataRef = useRef<string>('')
  const isSavingRef = useRef(false)

  // Save draft function
  const saveDraft = useCallback(async () => {
    if (!enabled || isSavingRef.current) {
      return
    }

    // Don't save if there's no meaningful data
    if (!formData.paymentPage.title && !formData.paymentFor) {
      return
    }

    try {
      isSavingRef.current = true
      setStatus('saving')

      // Transform form data to payment link format with draft status
      const paymentLinkData = transformFormDataToPaymentLink({
        ...formData,
        status: 'draft',
        isDraft: true
      })

      let savedId: string

      const DEFAULT_ORG_ID = envConfig.defaultOrgId
      const DEFAULT_ACCOUNT_ID = String(envConfig.defaultAccountId)

      if (currentDraftId) {
        // Update existing draft
        await paymentLinkService.updatePaymentLink(DEFAULT_ORG_ID, DEFAULT_ACCOUNT_ID, currentDraftId, paymentLinkData)
        savedId = currentDraftId
      } else {
        // Create new draft
        const result = await paymentLinkService.createPaymentLink(DEFAULT_ORG_ID, DEFAULT_ACCOUNT_ID, paymentLinkData)
        savedId = result.id!
        setCurrentDraftId(savedId)
      }

      const now = new Date()
      setLastSaved(now)
      setStatus('saved')

      // Call success callback
      onSaved?.(savedId)

      // Reset status to idle after 3 seconds
      setTimeout(() => {
        setStatus('idle')
      }, 3000)
    } catch (error) {
      console.error('Auto-save failed:', error)
      setStatus('error')
      onError?.(error as Error)

      // Reset status to idle after 5 seconds
      setTimeout(() => {
        setStatus('idle')
      }, 5000)
    } finally {
      isSavingRef.current = false
    }
  }, [enabled, formData, currentDraftId, onSaved, onError])

  // Manual save function
  const saveNow = useCallback(async () => {
    // Clear any pending debounced saves
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    await saveDraft()
  }, [saveDraft])

  // Debounced auto-save on form data changes
  useEffect(() => {
    if (!enabled) return

    const currentFormDataString = JSON.stringify(formData)

    // Check if form data actually changed
    if (currentFormDataString === previousFormDataRef.current) {
      return
    }

    previousFormDataRef.current = currentFormDataString

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      saveDraft()
    }, debounceDelay)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [formData, enabled, debounceDelay, saveDraft])

  // Periodic auto-save
  useEffect(() => {
    if (!enabled || !autoSaveInterval) return

    autoSaveTimerRef.current = setInterval(() => {
      // Only auto-save if there's been changes since last save
      const currentFormDataString = JSON.stringify(formData)
      if (currentFormDataString !== previousFormDataRef.current) {
        saveDraft()
      }
    }, autoSaveInterval)

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current)
      }
    }
  }, [enabled, autoSaveInterval, formData, saveDraft])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current)
      }
    }
  }, [])

  return {
    status,
    lastSaved,
    draftId: currentDraftId,
    saveNow,
    isSaving: status === 'saving'
  }
}
